// src/components/Seo.jsx
// One place for every page's SEO title, description and canonical link.
// Rendered once inside <Router> in App.js. No extra npm package needed.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import products from "../data/product";
import courses from "../data/courses";

const SITE = "https://astroseven.adityaxinnovations.com";

const HOME = {
  title: "Talk to Astrologer – Free Kundli & Rashifal | AstroSeven",
  description:
    "Talk to an AI astrologer online about marriage, career and love. Free kundli, daily horoscope and kundli matching on AstroSeven.",
};

// Keys are lowercase paths.
const PAGES = {
  "/": HOME,
  "/consultancy": {
    title: "Talk to Astrologer Online – Chat Now | AstroSeven",
    description:
      "Talk to an AI astrologer online about marriage, love, career and money. Choose a consultation and start your astrology chat on AstroSeven.",
  },
  "/free-kundali": {
    title: "Free Kundli Online in Hindi & English | AstroSeven",
    description:
      "Make your free kundli (janam kundli) online. Enter your birth date, time and place to see your birth chart in Hindi or English on AstroSeven.",
  },
  "/horoscopes": {
    title: "Aaj Ka Rashifal – Daily Horoscope | AstroSeven",
    description:
      "Read your daily horoscope (aaj ka rashifal) for all zodiac signs on AstroSeven.",
  },
  "/match-making": {
    title: "Kundli Matching for Marriage – Kundli Milan | AstroSeven",
    description:
      "Check kundli matching (kundli milan, gun milan) for marriage online. Enter both birth details and see your compatibility score on AstroSeven.",
  },
  "/aboutus": {
    title: "About AstroSeven – AI Astrology Chat",
    description:
      "AstroSeven lets you talk to an AI astrologer and get a free kundli, horoscope and kundli matching. Learn more about us.",
  },
  "/vipastrologer": {
    title: "VIP Astrologer Consultation | AstroSeven",
    description: "Book a VIP astrology consultation on AstroSeven.",
  },
  "/occult": {
    title: "Learn Astrology, Numerology & Vastu Courses | AstroSeven",
    description:
      "Explore online courses in astrology, numerology, vastu, tarot and more on AstroSeven.",
  },
  "/ganpati-sthapana": {
    title: "Ganpati Sthapana Booking Online | AstroSeven",
    description: "Book your Ganpati Sthapana puja online with AstroSeven.",
  },
  "/shopping": {
    title: "Rudraksha, Gemstones & Bracelets Online | AstroSeven",
    description:
      "Shop rudraksha, gemstones, healing bracelets, malas and yantras online at AstroSeven.",
  },
  "/shop/rudraksha": {
    title: "Rudraksha Online – Mukhi Rudraksha | AstroSeven",
    description: "Shop rudraksha beads and mukhi rudraksha online at AstroSeven.",
  },
  "/shop/gemstone": {
    title: "Astrological Gemstones Online | AstroSeven",
    description: "Shop astrology gemstones online at AstroSeven.",
  },
  "/shop/bracelet": {
    title: "Healing Crystal Bracelets Online | AstroSeven",
    description: "Shop crystal and healing bracelets online at AstroSeven.",
  },
  "/shop/mala": {
    title: "Rudraksha & Crystal Mala Online | AstroSeven",
    description: "Shop rudraksha mala and crystal mala online at AstroSeven.",
  },
  "/shop/yantra": {
    title: "Shree Yantra & Yantras Online | AstroSeven",
    description: "Shop Shree Yantra and other yantras online at AstroSeven.",
  },
  "/shop/healing-accessories": {
    title: "Healing Accessories & Crystals | AstroSeven",
    description: "Shop healing crystals and accessories online at AstroSeven.",
  },
  "/shop/misc": {
    title: "Astrology Products Online | AstroSeven",
    description: "Shop astrology and spiritual products online at AstroSeven.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | AstroSeven",
    description: "Read the AstroSeven privacy policy.",
  },
  "/terms-conditions": {
    title: "Terms & Conditions | AstroSeven",
    description: "Read the AstroSeven terms and conditions.",
  },
  "/refund-cancellation": {
    title: "Refund & Cancellation Policy | AstroSeven",
    description: "Read the AstroSeven refund and cancellation policy.",
  },
  "/disclaimer": {
    title: "Disclaimer | AstroSeven",
    description: "Read the AstroSeven disclaimer.",
  },
  "/pricing-policy": {
    title: "Pricing Policy | AstroSeven",
    description: "Read the AstroSeven pricing policy.",
  },
};

// Pages that should NOT appear in Google (same list as robots.txt).
const PRIVATE_PREFIXES = [
  "/login",
  "/user",
  "/astrologer/dashboard",
  "/astrologer/chat",
  "/astrologer/register",
  "/admin",
  "/chat",
  "/video-call",
  "/audio-call",
  "/payment",
  "/checkout",
  "/wallet",
  "/order-success",
  "/thank-you",
  "/ai-consultancy",
  "/astrochat",
  "/forgot-password",
  "/reset-password",
];

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function getMeta(path) {
  if (PAGES[path]) return PAGES[path];

  if (path.startsWith("/product/")) {
    const id = path.replace("/product/", "");
    const product = products.find((p) => p.id.toLowerCase() === id);
    if (product) {
      return {
        title: `${product.name} – Buy Online | AstroSeven`,
        description: `Buy ${product.name} online at AstroSeven. See price and details.`,
      };
    }
  }

  if (path.startsWith("/course/")) {
    const id = path.replace("/course/", "");
    const course = courses.find((c) => c.id.toLowerCase() === id);
    if (course) {
      return {
        title: `${course.title} Course Online | AstroSeven`,
        description: `Learn ${course.title} online with AstroSeven. See the course details, curriculum and how to enrol.`,
      };
    }
  }

  if (path.startsWith("/astrologer/")) {
    return {
      title: "Astrologer Profile | AstroSeven",
      description: "View an astrologer's profile on AstroSeven.",
    };
  }

  return HOME;
}

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = (pathname.replace(/\/+$/, "") || "/").toLowerCase();
    const { title, description } = getMeta(path);
    const url = SITE + (path === "/" ? "/" : path);
    const isPrivate = PRIVATE_PREFIXES.some((p) => path.startsWith(p));

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", isPrivate ? "noindex, nofollow" : "index, follow");
    setCanonical(url);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
  }, [pathname]);

  return null;
}